import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Camera,
  Image as ImageIcon,
  ArrowRight,
  ArrowLeft,
  Sparkles,
  Loader2,
  RefreshCw,
  CheckCircle2,
  ShieldCheck,
  Store,
  Calendar,
  Euro,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Client-side image compression / normalisation.
 * Ensures uploads stay < 8 MB and are always JPEG (handles HEIC too via the
 * browser's native decoder).
 */
async function prepareReceiptImage(file) {
  const MAX_DIM = 1800;
  const bitmap = await createImageBitmap(file);
  let { width, height } = bitmap;
  const scale = Math.min(1, MAX_DIM / Math.max(width, height));
  width = Math.round(width * scale);
  height = Math.round(height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(bitmap, 0, 0, width, height);
  const blob = await new Promise((resolve) =>
    canvas.toBlob(resolve, "image/jpeg", 0.86)
  );
  return new File([blob], "receipt.jpg", { type: "image/jpeg" });
}

export default function ReceiptUploadStep({ onComplete, onBack, merchantName }) {
  const { t } = useTranslation();
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null); // { url, path, ocr }

  function resetPreview() {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setResult(null);
  }

  async function handleFile(file) {
    if (!file) return;
    if (!/^image\//.test(file.type) && !/\.heic$/i.test(file.name)) {
      toast.error(t("play.receipt_error_type"));
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error(t("play.receipt_error_size"));
      return;
    }
    resetPreview();
    setPreviewUrl(URL.createObjectURL(file));
    try {
      setUploading(true);
      const normalised = await prepareReceiptImage(file);
      const form = new FormData();
      form.append("file", normalised);
      setAnalyzing(true);
      const { data } = await axios.post(`${API}/public/uploads/receipt`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
    } catch (e) {
      console.error(e);
      toast.error(t("play.receipt_error_network"));
      resetPreview();
    } finally {
      setUploading(false);
      setAnalyzing(false);
    }
  }

  function triggerCamera() {
    cameraRef.current?.click();
  }
  function triggerGallery() {
    galleryRef.current?.click();
  }

  return (
    <div className="fade-up" data-testid="receipt-upload-step">
      <div
        className="relative overflow-hidden rounded-2xl border border-white/10 bg-black/55 px-5 py-5 backdrop-blur-xl"
        style={{
          boxShadow:
            "inset 0 1px 0 rgba(255,255,255,0.08), 0 20px 40px -20px rgba(0,0,0,0.7)",
        }}
      >
        <div
          aria-hidden
          className="absolute left-0 top-0 h-full w-[2px]"
          style={{
            background:
              "linear-gradient(180deg, transparent, rgba(251,191,36,0.8), transparent)",
          }}
        />
        <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-amber-300">
          <Sparkles className="h-3.5 w-3.5" strokeWidth={2.5} />
          {t("play.receipt_eyebrow")}
        </div>
        <h2 className="mt-2 text-xl font-semibold tracking-tight text-white sm:text-2xl">
          {t("play.receipt_title")}
        </h2>
        <p className="mt-2 text-sm leading-relaxed text-white/80">
          {t("play.receipt_intro")}
        </p>
      </div>

      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
        data-testid="receipt-camera-input"
      />
      <input
        ref={galleryRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
        data-testid="receipt-gallery-input"
      />

      {!previewUrl && !result && (
        <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <button
            type="button"
            onClick={triggerCamera}
            disabled={uploading}
            className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-white/15 bg-black/45 px-4 py-6 text-sm font-medium text-white backdrop-blur-md transition-colors hover:border-white/25 hover:bg-black/60 disabled:opacity-50"
            data-testid="receipt-btn-camera"
          >
            <Camera className="h-6 w-6 text-amber-300" />
            {t("play.receipt_take_photo")}
          </button>
          <button
            type="button"
            onClick={triggerGallery}
            disabled={uploading}
            className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-white/15 bg-black/45 px-4 py-6 text-sm font-medium text-white backdrop-blur-md transition-colors hover:border-white/25 hover:bg-black/60 disabled:opacity-50"
            data-testid="receipt-btn-gallery"
          >
            <ImageIcon className="h-6 w-6 text-amber-300" />
            {t("play.receipt_pick_from_gallery")}
          </button>
        </div>
      )}

      {(previewUrl || result) && (
        <div className="mt-6 overflow-hidden rounded-2xl border border-white/15 bg-black/55 backdrop-blur-md">
          <div className="relative aspect-[3/4] w-full overflow-hidden bg-black/40">
            {previewUrl && (
              
              <img
                src={previewUrl}
                alt="Ticket preview"
                className="h-full w-full object-contain"
                data-testid="receipt-preview-img"
              />
            )}
            {(uploading || analyzing) && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/55 text-white backdrop-blur-sm">
                <Loader2 className="h-8 w-8 animate-spin" />
                <div className="text-sm font-medium">
                  {uploading
                    ? t("play.receipt_uploading")
                    : t("play.receipt_analyzing")}
                </div>
              </div>
            )}
          </div>

          {result && !uploading && !analyzing && (
            <div className="border-t border-white/10 bg-black/40 p-4 backdrop-blur-md">
              <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-emerald-300">
                <CheckCircle2 className="h-4 w-4" />
                {t("play.receipt_ready_title")}
              </div>
              <p className="mt-1 text-sm text-white/80">
                {t("play.receipt_ready_desc")}
              </p>
              {result.ocr && (
                <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {result.ocr.total_amount != null && (
                    <div className="flex items-center gap-1.5 rounded-lg border border-white/15 bg-white/10 px-2.5 py-1.5 text-xs text-white">
                      <Euro className="h-3 w-3 text-amber-300" />
                      <span className="font-semibold">
                        {Number(result.ocr.total_amount).toFixed(2)}
                      </span>
                      <span className="text-white/60">
                        {result.ocr.currency || "EUR"}
                      </span>
                    </div>
                  )}
                  {result.ocr.date && (
                    <div className="flex items-center gap-1.5 rounded-lg border border-white/15 bg-white/10 px-2.5 py-1.5 text-xs text-white">
                      <Calendar className="h-3 w-3 text-amber-300" />
                      <span>{result.ocr.date}</span>
                    </div>
                  )}
                  {result.ocr.merchant_name && (
                    <div className="col-span-2 flex items-center gap-1.5 rounded-lg border border-white/15 bg-white/10 px-2.5 py-1.5 text-xs text-white sm:col-span-1">
                      <Store className="h-3 w-3 text-amber-300" />
                      <span className="truncate">
                        {result.ocr.merchant_name}
                      </span>
                    </div>
                  )}
                </div>
              )}
              <button
                type="button"
                onClick={() => {
                  resetPreview();
                  setTimeout(triggerGallery, 50);
                }}
                className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-white/70 hover:text-white"
                data-testid="receipt-btn-replace"
              >
                <RefreshCw className="h-3 w-3" />
                {t("play.receipt_replace")}
              </button>
            </div>
          )}
        </div>
      )}

      <div className="mt-4 flex items-start gap-2 rounded-xl bg-black/45 px-3 py-2 text-[11px] text-white/80 backdrop-blur-md">
        <ShieldCheck className="mt-0.5 h-3.5 w-3.5 flex-none text-white/60" />
        <span>
          {t("play.receipt_privacy", {
            merchant: merchantName || t("landing.hero_line1"),
          })}
        </span>
      </div>

      <div className="mt-6 flex flex-col gap-2 sm:flex-row">
        <Button
          type="button"
          variant="outline"
          className="h-11 rounded-xl border-white/20 bg-black/40 text-white backdrop-blur-md hover:bg-black/55 hover:text-white"
          onClick={onBack}
          data-testid="receipt-btn-back"
        >
          <ArrowLeft className="mr-1.5 h-4 w-4" />
          {t("play.receipt_back")}
        </Button>
        <Button
          type="button"
          className="h-11 flex-1 rounded-xl bg-white text-black hover:bg-white/90 disabled:opacity-50"
          disabled={!result || uploading || analyzing}
          onClick={() => onComplete?.(result)}
          data-testid="receipt-btn-continue"
        >
          {t("play.receipt_continue")}
          <ArrowRight className="ml-1.5 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
